import { get, writable } from 'svelte/store';
import { Api } from '@automapa/svelte-automapa-template';
import { GuideApi, type GuideChapterType } from './GuideApi';

export const GUIDE_CHAPTER_URL =
    'https://guide.automapa.pl/books/last-mile/chapter/poradnik-uzytkownika';

export type GuideStateType = {
    chapters: GuideChapterType[];
    loading: boolean;
    loaded: boolean;
    error: string;
    /**
     * Current selection, kept in the store because the chapter select lives in the modal header
     * while the content is rendered by another component. It doubles as the position the modal
     * was closed at, so reopening lands exactly where the user left off.
     */
    selectedChapterKey: string;
    selectedPageSlug: string;
    scrollTop: number;
};

export const guide = writable<GuideStateType>({
    chapters: [],
    loading: false,
    loaded: false,
    error: '',
    selectedChapterKey: '',
    selectedPageSlug: '',
    scrollTop: 0,
});

/**
 * Slug of the documentation page describing the screen the user is currently on. A page registers
 * it in onMount and clears it in onDestroy - the modal is opened from the application header, which
 * knows nothing about the route below it.
 */
export const guidePageContext = writable<string>('');

export function setGuidePageContext(slug: string): void {
    guidePageContext.set(slug);
}

export function clearGuidePageContext(): void {
    guidePageContext.set('');
}

// Free-text search over the guide. liveQuery is the text currently in the input (two-way bound),
// searchQuery is the debounced value that actually drives the search - kept apart so the debounced
// value never overwrites characters the user is still typing. Held in the store because the input
// lives in the modal header while the results are rendered by DocumentationContent (same reason as
// the chapter selection above).
export const guideSearchLiveQuery = writable<string>('');
export const guideSearchQuery = writable<string>('');

export function clearGuideSearch(): void {
    guideSearchLiveQuery.set('');
    guideSearchQuery.set('');
}

const DOCUMENTATION_VISITED_KEY = 'documentationVisited';

/**
 * Whether the user has ever opened the documentation. Until they do, the help button is accented -
 * the accent must not come back on the next visit, hence localStorage.
 */
export const documentationVisited = writable<boolean>(
    typeof localStorage !== 'undefined' && localStorage.getItem(DOCUMENTATION_VISITED_KEY) === 'true'
);

export function markDocumentationVisited(): void {
    documentationVisited.set(true);

    if (typeof localStorage !== 'undefined') {
        localStorage.setItem(DOCUMENTATION_VISITED_KEY, 'true');
    }
}

let pending: Promise<void> | null = null;

/**
 * Fetches the whole guide once, at runtime. Repeated calls reuse the first request.
 */
export function loadDocumentation(api: Api, force: boolean = false): Promise<void> {
    const state = get(guide);

    if (force) {
        pending = null;
    } else {
        if (state.loaded) {
            return Promise.resolve();
        }
        if (pending) {
            return pending;
        }
    }

    guide.update(s => ({ ...s, loading: true, error: '' }));

    pending = GuideApi.getDocumentation(api)
        .then(response => {
            guide.update(s => ({
                ...s,
                chapters: response.chapters,
                loading: false,
                loaded: true,
                error: '',
            }));
        })
        .catch(e => {
            console.error('Guide, loadDocumentation', e);
            guide.update(s => ({
                ...s,
                loading: false,
                loaded: false,
                error: e?.message ?? String(e),
            }));
        })
        .finally(() => {
            pending = null;
        });

    return pending;
}

/**
 * Resets the store to its initial state and drops any cached fetch, so the next loadDocumentation()
 * re-fetches from the server and the modal shows the spinner again. Used by the debug "Reset cache"
 * action after the backend cache has been cleared.
 */
export function resetDocumentation(): void {
    pending = null;
    clearGuideSearch();
    guide.set({
        chapters: [],
        loading: false,
        loaded: false,
        error: '',
        selectedChapterKey: '',
        selectedPageSlug: '',
        scrollTop: 0,
    });
}

export function findChapter(state: GuideStateType, key: string): GuideChapterType | undefined {
    return state.chapters.find(chapter => chapter.key === key);
}

/**
 * Switching a chapter opens its first page.
 */
export function selectChapter(key: string): void {
    guide.update(s => {
        const chapter = findChapter(s, key);
        if (!chapter || chapter.pages.length === 0) {
            return s;
        }

        return {
            ...s,
            selectedChapterKey: key,
            selectedPageSlug: chapter.pages[0].slug,
            scrollTop: 0,
        };
    });
}

export function selectPage(slug: string): void {
    guide.update(s => ({ ...s, selectedPageSlug: slug, scrollTop: 0 }));
}

export function rememberScroll(scrollTop: number): void {
    guide.update(s => ({ ...s, scrollTop }));
}
